<template>
	<TheForm :model="request" :rules="rules" ref="formRef" :waiting="waiting" :id="id">
		<ButtonSticky @buttonclick="createUser()">
			<template v-slot:icon><saveIcon /></template>
			<template v-slot:text> Speichern </template>
		</ButtonSticky>

		<section>
			<ButtonBack />

			<el-row>
				<h1>Neuer User</h1>
			</el-row>

			<TheBox :title="'User Daten'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="E-Mail:" prop="email">
							<el-input v-model="request.email" :placeholder="'Bitte Email eingeben'" />
						</el-form-item>

						<el-form-item label="Erstellt am:" prop="">
							<span>-</span>
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Letzter Login:" prop="">
							<span>-</span>
						</el-form-item>

						<el-form-item label="ID:" prop="">
							<span>-</span>
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>

			<TheBox :title="'User Daten'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Anrede:" class="datagrid--input-short" prop="gender">
							<el-select v-model="request.gender" :placeholder="'Bitte wählen'">
								<el-option v-for="option in genderOptions" :key="option.value" :label="option.label" :value="option.value" />
							</el-select>
						</el-form-item>

						<el-form-item label="Titel (vor):" class="datagrid--input-short" prop="titleBefore">
							<el-input v-model="request.titleBefore" :placeholder="'Bitte Titel eingeben'" />
						</el-form-item>

						<el-form-item label="Titel (nach):" class="datagrid--input-short" prop="titleAfter">
							<el-input v-model="request.titleAfter" :placeholder="'Bitte Titel eingeben'" />
						</el-form-item>

						<el-form-item label="Vorname:" prop="firstName">
							<el-input v-model="request.firstName" :placeholder="'Bitte Vorname eingeben'" />
						</el-form-item>

						<el-form-item label="Nachname:" prop="lastName">
							<el-input v-model="request.lastName" :placeholder="'Bitte Nachname eingeben'" />
						</el-form-item>

						<el-form-item label="Initialen:" class="datagrid--input-short" prop="initials">
							<el-input v-model="request.initials" :placeholder="'Bitte Initialen eingeben'" />
						</el-form-item>
					</el-col>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Pro-Ethik-Siegel:" prop="seal">
							<TheToggle :active="request.seal" @toggle="request.seal = !request.seal" />
						</el-form-item>

						<el-form-item label="Firma" prop="company">
							<el-input v-model="request.company" :placeholder="'Bitte Firma eingeben'" />
						</el-form-item>

						<el-form-item label="Telefon" prop="phoneNumber">
							<el-input v-model="request.phoneNumber" :placeholder="'Bitte Telefonnummer eingeben'" />
						</el-form-item>

						<el-form-item label="Straße:" prop="street">
							<el-input v-model="request.street" :placeholder="'Bitte Straße eingeben'" />
						</el-form-item>

						<el-form-item label="PLZ:" prop="postalCode">
							<el-input v-model="request.postalCode" :placeholder="'Bitte PLZ eingeben'" />
						</el-form-item>

						<el-form-item label="Ort:" prop="city">
							<el-input v-model="request.city" :placeholder="'Bitte Ort eingeben'" />
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>
		</section>

		<section class="no-marginbottom">
			<el-row>
				<el-col :sm="12" :xs="24">
					<h2>Gruppenzugehörigkeit</h2>
				</el-col>
				<el-col :sm="12" :xs="24" class="flex flex--justifyend">
					<TheButton @buttonclick="openDialogNewRole = true">
						<el-icon><Plus /></el-icon>Neue Gruppenzugehörigkeit
					</TheButton>
				</el-col>
			</el-row>
		</section>

		<div v-show="userRoleError" class="el-form-item__error">{{ texts.formErrors.userRoleMissing }}</div>
		<RoleSlider :roles="request.roles ? request.roles : []" @delete="(role) => removeRoleFromUser(role)" />

		<!-- needed for form validation -->
		<el-form-item label="" prop="roles" class="is-hidden">
			<el-input v-model="request.roles" />
		</el-form-item>

		<section>
			<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
				<el-input v-model="request.internalNote" :autosize="{ minRows: 3 }" type="textarea" placeholder="Interne Notiz" />
			</TheBox>
		</section>
	</TheForm>

	<DialogNewRole v-model:open="openDialogNewRole" @addNewRole="(role) => addRoleToUser(role)" :roleOptions="availableRoles" />
	<DialogLeavePage v-model:open="openClosingDialog" @cancel="leavePage" @save="createUser(true)" />
</template>

<script lang="ts">
import CasesTable from '@/components/tables/CasesTable.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';
import DialogLeavePage from '../components/dialogs/DialogLeavePage.vue';
import { Delete, Plus, Loading } from '@element-plus/icons-vue';
import { defineComponent, ref, markRaw } from 'vue';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import TheToggle from '@/components/TheToggle.vue';
import BoxRole from '@/components/boxes/BoxRole.vue';
import DialogEditUserAbsence from '@/components/dialogs/DialogEditUserAbsence.vue';
import DialogHandleUserActivation from '@/components/dialogs/DialogHandleUserActivation.vue';
import DialogNewRole from '@/components/dialogs/DialogNewRole.vue';
import InputLite from '@/components/forms/InputLite.vue';
import { categories } from '@/utils/categories';
import { returnFormOptions } from '@/utils/formOptions';
import { RegisterRequest, RoleDto, UserStatus } from '../types/types';
import { texts } from '../utils/texts';
import { register } from '@/requests/auth';
import { ElMessage, FormInstance } from 'element-plus';
import { validateForm, hasFormChanged, resetForm } from '@/utils/functions';

const ElMessageDuration = 3000;

const initialRequestObj = {
	email: '',
	firstName: '',
	lastName: '',
	gender: null,
	seal: false,
	postalCode: '',
	city: '',
	street: '',
	phoneNumber: '',
	company: '',
	titleBefore: '',
	titleAfter: '',
	initials: '',
	roles: [] as RoleDto[],
	internalNote: '',
} as RegisterRequest;

export default defineComponent({
	name: 'UserNeuView',
	data() {
		return {
			request: initialRequestObj,
			initialData: JSON.parse(JSON.stringify(initialRequestObj)), // deep copy to ensure different objects
			rules: {
				gender: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				email: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ type: 'email', message: texts.formErrors.emailReq, trigger: ['blur'] },
				],
				firstName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				lastName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				initials: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ min: 2, message: texts.formErrors.minChars, trigger: 'blur' },
					{ max: 3, message: texts.formErrors.maxChars, trigger: 'blur' },
				],
			},
			userRoleError: false,
			id: 'userForm',
			waiting: false,
			loading: true,
			categories,
			openEditAbsenceModal: false,
			openDialogNewRole: false,
			openDialogHandleUserActivation: false,
			openClosingDialog: false,
			checkPageLeave: true,
			pageLeaveTo: null,
			UserStatus,
		};
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		Loading,
		TheBox,
		TheToggle,
		CasesTable,
		Plus,
		Delete,
		saveIcon,
		BoxRole,
		DialogHandleUserActivation,
		DialogNewRole,
		DialogEditUserAbsence,
		InputLite,
		RoleSlider,
		DialogLeavePage,
	},
	setup() {
		const formRef = ref<FormInstance>();

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		genderOptions() {
			return returnFormOptions('gender');
		},
		availableRoles() {
			return returnFormOptions('userRole').filter((role) => !this.request.roles.some((userRole) => userRole.name === role.value?.name));
		},
	},
	methods: {
		async createUser(leavePage = false) {
			this.openClosingDialog = false;
			this.userRoleError = false;

			let isValid = await validateForm(this.formRef, this.id);
			if (this.request.roles.length === 0) {
				isValid = false;
				this.userRoleError = true;
			}

			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const result = await register(this.request);
			if (result.success) {
				if (leavePage) this.leavePage();
				else {
					this.checkPageLeave = false;
					this.$router.push({ name: 'UserDetail', params: { id: result.data.id }, replace: true });
				}
			} else {
				if (result.error.detail === 'A user with this email address already exists.') {
					ElMessage({ message: texts.user.emailExists, type: 'error', duration: ElMessageDuration });
				} else {
					ElMessage({ message: texts.user.createUserFailed, type: 'error', duration: ElMessageDuration });
				}
			}

			this.waiting = false;
			waitingMsg.close();
		},

		addRoleToUser(role) {
			this.request.roles.push({
				name: role.value.name,
			});
			this.userRoleError = false;
		},
		removeRoleFromUser(role) {
			this.request.roles = this.request.roles.filter((r) => r.name !== role.name);
		},
		leavePage() {
			this.openClosingDialog = false;
			this.checkPageLeave = false;
			// @ts-ignore
			this.$router.push(this.pageLeaveTo);
		},
		handleBeforeRouteLeave(to, from, next) {
			if (this.checkPageLeave === false) {
				resetForm(this.formRef, this.id);
				next();
				return;
			}

			// check if request data has changed
			const hasChanged = hasFormChanged({ initialData: this.initialData, currentData: this.request });

			if (!hasChanged) {
				next();
				return;
			}

			// ABORT NAVIGATION & SHOW DIALOG
			this.openClosingDialog = true;
			this.pageLeaveTo = to;
			next(false);
		},
	},
	beforeRouteLeave(to, from, next) {
		this.handleBeforeRouteLeave(to, from, next);
	},
	mounted() {},
});
</script>
